import {useAuthDataContext} from "../../components/AuthDataProvider";
import {Fragment, useEffect, useState, useMemo, useCallback} from "react";
import { useScrollbarSync } from "../../hooks/useScrollbarSync";
import { usePaginatedSearch } from "../../hooks/usePaginatedSearch";
import {CircularProgress, TableCell, TableRow, Grid, Paper, Table, TableBody, Icon, makeStyles} from "@material-ui/core";
import ArtApi from "../../api/ArtApi";
import Button from "@material-ui/core/Button";
import { MultiSelectFilter } from "../../components/MultiSelectFilter";
import { SearchBar } from "../../components/SearchBar";
import ErrorSnackbar from "../../components/ErrorSnackbar";
import { buildCsvRow } from "../../lib/csv";

const useStyles = makeStyles({
    scrollWrapper: {
        width: '100%',
    },
    topScrollbar: {
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        backgroundColor: '#424242',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
    },
    topScrollbarInner: {
        height: '1px',
    },
    tableContainer: {
        width: '100%',
        overflowX: 'auto',
        borderTopLeftRadius: '0 !important',
        borderTopRightRadius: '0 !important',
        /* Hide scrollbar for Chrome, Safari and Opera */
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        /* Hide scrollbar for IE, Edge and Firefox */
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
    },
    table: {
        minWidth: 'max-content',
        '& td, & th': {
            fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)',
            padding: '8px 12px',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
        },
    }
});

export const RheemIndicators = () => {
    const classes = useStyles();
    const { user } = useAuthDataContext();
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [indicators, setindicators] = useState<any | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [indicatorTypes, setindicatorTypes] = useState<string[]>([]);

    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [sort, setSort] = useState("");
    const [excludeTypes, setExcludeTypes] = useState<string[]>([]);
    const { topScrollRef, bottomScrollRef, topScrollInnerRef } = useScrollbarSync(indicators);

    /**
     * Reload all the indicators.
     */
    function loadIndicators() {
        setIsLoading(true);
        ArtApi.getRheemIndicators(committedSearchTerm, sort, page, 25, excludeTypes)
            .then(response => {
                setIsLoading(false);
                if (response.error) {
                    setError(response.error);
                    setSnackbarOpen(true);
                }

                let { total, page, indicators, pageSize, types } = response.response;
                setTotalPages(Math.ceil(total / pageSize));
                setTotal(total);
                setPage(page);
                setindicators(indicators);
                setindicatorTypes(types);
            }).catch(error => {
                setIsLoading(false);
                setError(error.message);
                setSnackbarOpen(true);
            });
    }

    const { searchTerm, setSearchTerm, committedSearchTerm, doSearch, clearSearch } = usePaginatedSearch({
        page, setPage, load: loadIndicators
    });

    /**
     * When the page, sort, excludeTypes, or committedSearchTerm states are changed, we need
     * to reload the indicators.
     */
    useEffect(() => {
        loadIndicators();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, sort, excludeTypes, committedSearchTerm]);

    const renderRows = useMemo(() => {
        if (indicators) {
            return indicators.map((indicator: any) => {
                // dates are of form '2025-02-14T16:24:48.351000Z'
                const duration = indicator.end ? (new Date(indicator.end).getTime() - new Date(indicator.start).getTime()) / 1000 : null;

                let durationString = "";
                if (duration) {
                    const hours = Math.floor(duration / 3600);
                    const minutes = Math.floor((duration % 3600) / 60);
                    durationString = `${hours}h ${minutes}m`;
                }

                // Display times in UTC
                const start = new Date(indicator.start).toLocaleString("en-US", { timeZone: 'UTC' }) + ' UTC';
                const end = indicator.end ? new Date(indicator.end).toLocaleString("en-US", { timeZone: 'UTC' }) + ' UTC' : "";

                // linkify coreID
                const coreID = <a href={`/rheem/${indicator.coreID}`}>{indicator.coreID}</a>;
                const mac = <a href={`/rheem/${indicator.coreID}`}>{indicator.mac}</a>;

                return (
                    <TableRow key={indicator.id}>
                        <TableCell>{indicator.type}</TableCell>
                        <TableCell>{mac}</TableCell>
                        <TableCell>{coreID}</TableCell>
                        <TableCell>{start}</TableCell>
                        <TableCell>{end}</TableCell>
                        <TableCell>{durationString}</TableCell>
                    </TableRow>
                )
            })
        }
    }, [indicators]);

    // Type transformer for grouping R_ alarms (no "All" option needed with new logic)
    const indicatorTypeTransformer = useCallback((types: string[]) => {
        const R_REGEX = /^R_/;
        let typesSet = new Set(types.map((type: string) => {
            if (R_REGEX.test(type)) {
                return "RheemAlarms";
            }
            return type;
        }));
        return typesSet;
    }, []);

    // Handler for transformed type toggles
    // With new logic: checked = filter FOR this type (exclude all others)
    const handleTransformedTypeToggle = useCallback((type: string, isCurrentlyChecked: boolean, allTypes: string[], currentExcludeTypes: string[]) => {
        const R_REGEX = /^R_/;

        if (type === "RheemAlarms") {
            if (isCurrentlyChecked) {
                // Unchecking: remove filter for R_ types
                // Need to recalculate what should be excluded based on other checked items
                return null; // Use default behavior
            } else {
                // Checking: filter FOR R_ types (exclude non-R_ types)
                const nonRheemTypes = allTypes.filter(t => !R_REGEX.test(t));

                // If this is the only filter, exclude everything except R_ types
                if (currentExcludeTypes.length === 0) {
                    return nonRheemTypes;
                }

                // Otherwise, use default behavior
                return null;
            }
        }

        return null; // Use default behavior for other types
    }, []);

    // Function to determine if a transformed type is checked
    // With new logic: checked means we're filtering FOR this type
    const isTransformedTypeSelected = useCallback((type: string, allTypes: string[], currentExcludeTypes: string[]) => {
        const R_REGEX = /^R_/;

        if (type === "RheemAlarms") {
            // Checked if at least one R_ type is NOT excluded
            return !currentExcludeTypes.some(t => R_REGEX.test(t));
        }

        return !currentExcludeTypes.includes(type);
    }, []);

    const renderCell = useCallback((label, key, sortable=true) => {
        const style = {
            cursor: 'pointer',
            color: 'lightblue',
        }
        const handleHeaderClick = (column: string) => {
            if (sort === column) {
                setSort(`-${column}`);
            } else {
                setSort(column);
            }

            // reset page to 1
            setPage(1);
        }

        const renderSortIcon = (column: string) => {
            if (sort === column) {
                return <span>&#x25B2;</span>
            } else if (sort === `-${column}`) {
                return <span>&#x25BC;</span>
            }
            return null;
        }

        if (sortable) {
            return (
                <TableCell onClick={() => handleHeaderClick(key)} style={style}>
                    {label} {renderSortIcon(key)}
                </TableCell>
            )
        } else {
            return <TableCell>{label}</TableCell>
        }
    }, [sort]);

    const renderExportButton = useCallback(() => {
        return (
            <TableCell>
            <Button onClick={() => {
                setIsExporting(true);

                ArtApi.getRheemIndicators(committedSearchTerm, sort, 1, total, excludeTypes).then(response => {
                    const allIndicators = response.response.indicators;
                    let csv = "data:text/csv;charset=utf-8,";
                    csv += buildCsvRow(["Indicator Type", "MAC Address", "Core ID", "Start Time", "End Time", "Duration"]);
                    allIndicators.forEach((indicator: any) => {
                        const duration = indicator.end ? (new Date(indicator.end).getTime() - new Date(indicator.start).getTime()) / 1000 : null;
                        let durationString = "";
                        if (duration) {
                            const hours = Math.floor(duration / 3600);
                            const minutes = Math.floor((duration % 3600) / 60);
                            durationString = `${hours}h ${minutes}m`;
                        }

                        const start = new Date(indicator.start).toLocaleString("en-US", { timeZone: 'UTC' }) + ' UTC';
                        const end = indicator.end ? new Date(indicator.end).toLocaleString("en-US", { timeZone: 'UTC' }) + ' UTC' : "";

                        csv += buildCsvRow([indicator.type, indicator.mac, indicator.coreID, start, end, durationString]);
                    });

                    const encodedUri = encodeURI(csv);
                    const link = document.createElement("a");
                    link.setAttribute("href", encodedUri);
                    link.setAttribute("download", "rheem_indicators.csv");
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setIsExporting(false);
                })

            }} variant="outlined" disabled={isExporting}>{isExporting ? "Exporting..." : "Export"}</Button>
            </TableCell>
        )
    }, [isExporting, sort, excludeTypes, total, committedSearchTerm]);

    const renderPaginationRow = useMemo(() => {
        if (totalPages === 0) {
            return null;
        }

        let prevArrow;
        if (page === 1) {
            prevArrow = <span style={{ color: 'lightgrey', fontSize: '2em' }}>&#x25C0;</span>;
        } else {
            prevArrow = <span style={{ cursor: 'pointer', fontSize: '2em' }}>&#x25C0;</span>;
        }

        let nextArrow;
        if (page >= totalPages) {
            nextArrow = <span style={{ color: 'lightgrey', fontSize: '2em' }}>&#x25B6;</span>;
        } else {
            nextArrow = <span style={{ cursor: 'pointer', color: 'lightblue', fontSize: '2em' }}>&#x25B6;</span>;
        }

        return (
            <TableRow>
                <TableCell colSpan={7}>
                    <span onClick={() => setPage(page - 1)}>{prevArrow}</span>
                    <span style={{ fontSize: '2em' }}> {page} / {totalPages} </span>
                    <span onClick={() => setPage(page + 1)}>{nextArrow}</span>
                    <span> {total} results</span>
                </TableCell>
            </TableRow>
        )
    }, [totalPages, page, total]);

    return (
        <Fragment>
            {isLoading && <CircularProgress color="secondary" />}
            <ErrorSnackbar message={error} open={snackbarOpen} onClose={() => setSnackbarOpen(false)} />

            {!isLoading && indicators &&
                <Fragment>
                    <Grid container spacing={3} alignItems="center">
                        <SearchBar
                            placeholder="Search for indicator"
                            searchTerm={searchTerm}
                            onSearchTermChange={setSearchTerm}
                            onSearch={doSearch}
                            onClearSearch={clearSearch}
                        />
                        <MultiSelectFilter
                            items={indicatorTypes}
                            excludeItems={excludeTypes}
                            onExcludeItemsChange={setExcludeTypes}
                            label="Filter by Indicator Type"
                            itemTransformer={indicatorTypeTransformer}
                            onTransformedItemToggle={handleTransformedTypeToggle}
                            isTransformedItemSelected={isTransformedTypeSelected}
                        />
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <div className={classes.scrollWrapper}>
                                <div ref={topScrollRef} className={`${classes.topScrollbar} custom-scrollbar`}>
                                    <div ref={topScrollInnerRef} className={classes.topScrollbarInner}></div>
                                </div>
                                <Paper ref={bottomScrollRef} className={classes.tableContainer}>
                                    <Table className={classes.table}>
                                        <TableBody>
                                            <TableRow>
                                                {renderExportButton()}
                                            </TableRow>
                                            <TableRow>
                                                {renderCell("Indicator Type", "type")}
                                                {renderCell("MAC Address", "mac")}
                                                {renderCell("Core ID", "coreID")}
                                                {renderCell("Start Time", "start")}
                                                {renderCell("End Time", "end")}
                                                {renderCell("Duration", "duration")}
                                            </TableRow>
                                            {renderRows}
                                            {renderPaginationRow}
                                        </TableBody>
                                    </Table>
                                </Paper>
                            </div>
                        </Grid>
                    </Grid>
                </Fragment>
            }
        </Fragment>
    )
}
